import {useEffect} from 'react';
import {useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import {boolean, object} from 'yup';

import {Button, Form, Text, Toggle} from '#component/base';
import {sprinkles} from '#style/sprinkles';
import {handleValidationError} from '#util';
import {ValidationError} from '#util/validator/handleValidationError';

import {FieldValues, Props, SubmitHandler} from './NotificationsForm.types';

export const NotificationsForm = ({
  className,
  onSubmit,
  receiveEmailNotification = false,
  receiveSMSNotification = false,
}: Props) => {
  const {
    clearErrors,
    formState: {errors, isSubmitting},
    handleSubmit: hookFormHandleSubmit,
    register,
    reset,
    setError,
  } = useForm<FieldValues>({
    defaultValues: {receiveEmailNotification, receiveSMSNotification},
    mode: 'onBlur',
    resolver: yupResolver(
      object({
        receiveEmailNotification: boolean().required(),
        receiveSMSNotification: boolean().required(),
      }),
    ),
  });

  const handleSubmit: SubmitHandler = async ({
    receiveEmailNotification,
    receiveSMSNotification,
  }) => {
    try {
      await onSubmit?.({receiveEmailNotification, receiveSMSNotification});
    } catch (err) {
      handleValidationError({error: err as unknown as ValidationError<FieldValues>, setError});
    }
  };

  useEffect(() => {
    // If the default values provided via props change, reset the form's default values.
    reset({form: '', receiveEmailNotification, receiveSMSNotification});
  }, [receiveEmailNotification, receiveSMSNotification, reset]);

  return (
    <Form
      className={className}
      clearErrors={clearErrors}
      errors={errors}
      onSubmit={hookFormHandleSubmit(handleSubmit)}>
      <div>
        <label className={sprinkles({flex: 'flex', content: 'between', items: 'center', pY: 24})}>
          <Text>Email notifications</Text>
          <Toggle {...register('receiveEmailNotification')} />
        </label>

        <label className={sprinkles({flex: 'flex', content: 'between', items: 'center', pY: 24})}>
          <Text>SMS notifications</Text>
          <Toggle {...register('receiveSMSNotification')} />
        </label>
      </div>

      <Button disabled={isSubmitting} icon="Check" type="submit">
        Confirm
      </Button>
    </Form>
  );
};
